import type { Congelamento, Enforcement, Intervalo } from '@/features/changes/tipos';

/**
 * A aritmética do calendário de mudanças.
 *
 * Mora fora dos componentes porque é exatamente o que vale a pena testar: em
 * que célula cada janela cai, que dias estão congelados, onde a barra começa e
 * onde termina. Cada uma tem uma resposta certa, e nenhuma precisa de DOM para
 * ser verificada.
 *
 * Tudo aqui trabalha em **horário local**. Um calendário de manutenção existe
 * para responder "que noite?", e a noite é a de quem opera — converter para UTC
 * faria a janela das 22h de 30 de setembro aparecer em 1º de outubro.
 */

/** Intervalo meio-aberto `[inicio, fim)`, como o domínio do servidor o define. */
export interface Periodo {
  readonly inicio: Date;
  readonly fim: Date;
}

const DIA_EM_MS = 24 * 60 * 60 * 1000;

/**
 * `[a, b)` e `[c, d)` se sobrepõem quando `a < d` e `c < b`.
 *
 * Meio-aberto, igual a `changes/domain/conflitos.ts`: uma janela que termina às
 * 02:00 e outra que começa às 02:00 não colidem, e o calendário não deve
 * pintá-las como se colidissem.
 */
export function seSobrepoem(a: Intervalo, b: Intervalo): boolean {
  return a.inicio < b.fim && b.inicio < a.fim;
}

/** O mês inteiro da data de referência: `[1º 00:00, 1º do mês seguinte 00:00)`. */
export function mesDe(referencia: Date): Periodo {
  const inicio = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
  const fim = new Date(referencia.getFullYear(), referencia.getMonth() + 1, 1);
  return { inicio, fim };
}

/** O mês seguinte ou o anterior, preservando só ano e mês. */
export function deslocarMes(referencia: Date, meses: number): Date {
  return new Date(referencia.getFullYear(), referencia.getMonth() + meses, 1);
}

/**
 * As semanas que cobrem o mês, de domingo a sábado.
 *
 * Sempre começa no domingo anterior ao dia 1º e termina no sábado posterior ao
 * último dia: é assim que o calendário de parede brasileiro se lê, e uma grade
 * que começasse no dia 1º deixaria a primeira semana torta.
 *
 * O número de linhas varia entre 4 e 6 — fixar em 6 acrescentaria uma faixa
 * vazia em fevereiro, que é espaço tirado das janelas que existem.
 */
export function semanasDoMes(referencia: Date): Date[][] {
  const { inicio, fim } = mesDe(referencia);

  const primeiro = new Date(inicio);
  primeiro.setDate(primeiro.getDate() - primeiro.getDay());

  const semanas: Date[][] = [];
  const cursor = new Date(primeiro);
  // O cursor anda de sete em sete dias a partir de um domingo, então ele é
  // sempre domingo quando a condição é avaliada — e a condição é uma só: a
  // linha existe se ela **começa** antes do fim do mês. Uma semana que abre em
  // 1º de outubro não pertence à grade de setembro.
  while (cursor < fim) {
    const semana: Date[] = [];
    for (let i = 0; i < 7; i += 1) {
      semana.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    semanas.push(semana);
  }
  return semanas;
}

/**
 * O período que a grade realmente desenha — do domingo anterior ao dia 1º ao
 * sábado posterior ao último dia.
 *
 * Existe porque o mês e a grade **não** são o mesmo intervalo: setembro de 2026
 * cabe em cinco linhas que começam em 30 de agosto e terminam em 3 de outubro,
 * e até doze dias visíveis ficam fora de `mesDe`. Consultar o calendário pelo
 * mês e desenhar a grade faria esses dias aparecerem limpos — sem hachura de
 * congelamento, sem janela — numa tela cuja primeira pergunta é "o ambiente
 * está congelado?". Mostrar dia congelado como livre é o único erro que
 * importa aqui.
 */
export function periodoVisivel(referencia: Date): Periodo {
  const semanas = semanasDoMes(referencia);
  const primeiraSemana = semanas[0] as Date[];
  const ultimaSemana = semanas[semanas.length - 1] as Date[];
  const primeiro = primeiraSemana[0] as Date;
  const ultimo = ultimaSemana[6] as Date;
  return {
    inicio: new Date(primeiro.getFullYear(), primeiro.getMonth(), primeiro.getDate()),
    fim: new Date(ultimo.getFullYear(), ultimo.getMonth(), ultimo.getDate() + 1),
  };
}

/** O dia inteiro, em milissegundos: `[00:00, 24:00)` no fuso local. */
export function faixaDoDia(dia: Date): Intervalo {
  const inicio = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate());
  const fim = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate() + 1);
  return { inicio: inicio.getTime(), fim: fim.getTime() };
}

export function mesmoMes(dia: Date, referencia: Date): boolean {
  return dia.getMonth() === referencia.getMonth() && dia.getFullYear() === referencia.getFullYear();
}

export function ehHoje(dia: Date, agora = new Date()): boolean {
  return (
    dia.getDate() === agora.getDate() &&
    dia.getMonth() === agora.getMonth() &&
    dia.getFullYear() === agora.getFullYear()
  );
}

/** Um registro com começo e fim em ISO — janela, congelamento ou PSO. */
interface ComIntervalo {
  startsAt: string;
  endsAt: string;
}

export function intervaloDe(registro: ComIntervalo): Intervalo {
  return { inicio: new Date(registro.startsAt).getTime(), fim: new Date(registro.endsAt).getTime() };
}

/**
 * Os registros que tocam este dia — não só os que **começam** nele.
 *
 * A distinção é a razão de a função existir: manutenção começa às 22h e termina
 * às 02h do dia seguinte, e um calendário que a mostrasse só no primeiro dia
 * faria quem olhasse a madrugada de sábado concluir que não há nada marcado.
 */
export function noDia<T extends ComIntervalo>(dia: Date, registros: readonly T[]): T[] {
  const faixa = faixaDoDia(dia);
  return registros.filter((registro) => seSobrepoem(intervaloDe(registro), faixa));
}

/**
 * O congelamento mais duro que alcança este dia, ou `null`.
 *
 * `BLOCKING` ganha de `ADVISORY` quando os dois pegam o mesmo dia: pintar o dia
 * de "apenas informativo" quando existe um bloqueio é a única forma de errar
 * que importa aqui.
 *
 * Congelamento desligado (`active: false`) não pinta nada — ele continua no
 * histórico, mas não vale mais para agendar.
 */
export function congelamentoDoDia(
  dia: Date,
  congelamentos: readonly Congelamento[],
): Enforcement | null {
  const faixa = faixaDoDia(dia);
  let achado: Enforcement | null = null;
  for (const congelamento of congelamentos) {
    if (!congelamento.active) continue;
    if (!seSobrepoem(intervaloDe(congelamento), faixa)) continue;
    if (congelamento.enforcement === 'BLOCKING') return 'BLOCKING';
    achado = 'ADVISORY';
  }
  return achado;
}

/** Os congelamentos vigentes que alcançam este dia, para o texto do título. */
export function congelamentosDoDia(
  dia: Date,
  congelamentos: readonly Congelamento[],
): Congelamento[] {
  const faixa = faixaDoDia(dia);
  return congelamentos.filter((c) => c.active && seSobrepoem(intervaloDe(c), faixa));
}

/** Abaixo desta largura a barra deixa de existir na tela. Em porcentagem. */
const LARGURA_MINIMA = 1;

export interface PosicaoNaFaixa {
  /** Porcentagem da largura, de 0 a 100. */
  readonly esquerda: number;
  readonly largura: number;
}

/**
 * Onde uma barra começa e termina dentro do período visível.
 *
 * Recorta no período: um congelamento que começou em dezembro e termina em
 * janeiro aparece encostado na borda esquerda da faixa de janeiro, e não com
 * largura negativa. Largura mínima de 1% para que um bloqueio de duas horas
 * num mês inteiro continue sendo um pixel visível — uma barra de 0,3% não
 * existe na tela, e um congelamento invisível é pior que nenhum.
 *
 * ## Quem cede é a margem esquerda, e não a largura mínima
 *
 * Perto da borda direita as duas exigências brigam: um bloqueio das 22h do dia
 * 30 começa a 99,7% da faixa, e só sobram 0,3% até o fim dela. Cortar a largura
 * para caber devolveria exatamente a barra invisível que o mínimo existe para
 * impedir — e é o dia 30 que mais interessa, porque é o que ninguém vê ao
 * conferir o mês. Então a barra mantém o 1% e **desliza** para a esquerda o
 * tanto que precisar para não vazar.
 *
 * O deslize custa até 1% de precisão de posição, o que é aceitável porque a
 * faixa não tem eixo: ela é uma trilha lisa, com as datas exatas escritas ao
 * lado, e quem pergunta "que dia?" lê na grade, que é exata.
 */
export function posicaoNaFaixa(intervalo: Intervalo, periodo: Periodo): PosicaoNaFaixa {
  const inicioDoPeriodo = periodo.inicio.getTime();
  const duracao = periodo.fim.getTime() - inicioDoPeriodo;
  if (duracao <= 0) return { esquerda: 0, largura: 0 };

  const inicio = Math.max(intervalo.inicio, inicioDoPeriodo);
  const fim = Math.min(intervalo.fim, periodo.fim.getTime());
  if (fim <= inicio) return { esquerda: 0, largura: 0 };

  const largura = Math.min(Math.max(((fim - inicio) / duracao) * 100, LARGURA_MINIMA), 100);
  const esquerda = Math.min(((inicio - inicioDoPeriodo) / duracao) * 100, 100 - largura);
  return { esquerda, largura };
}

const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

/**
 * "22:00 – 02:00 (+1)" — a forma como uma janela de manutenção se escreve.
 *
 * O `(+n)` não é enfeite: sem ele, "22:00 – 02:00" é ambíguo entre uma janela
 * de quatro horas e uma de vinte, e as duas coisas se planejam diferente.
 */
export function rotuloDeHorario(inicioIso: string, fimIso: string): string {
  const inicio = new Date(inicioIso);
  const fim = new Date(fimIso);
  const dias = Math.round(
    (new Date(fim.getFullYear(), fim.getMonth(), fim.getDate()).getTime() -
      new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()).getTime()) /
      DIA_EM_MS,
  );
  const sufixo = dias > 0 ? ` (+${dias})` : '';
  return `${HORA.format(inicio)} – ${HORA.format(fim)}${sufixo}`;
}

/** Duração em minutos, para a tela dizer quanto tempo a janela dura. */
export function duracaoEmMinutos(inicioIso: string, fimIso: string): number {
  return Math.round((new Date(fimIso).getTime() - new Date(inicioIso).getTime()) / 60_000);
}

/**
 * O valor de um `<input type="datetime-local">` a partir de um ISO.
 *
 * `toISOString().slice(0, 16)` seria UTC, e o campo mostraria 01:00 para uma
 * janela das 22:00 — três horas de diferença que ninguém percebe até a
 * manutenção começar na hora errada.
 */
export function paraCampoLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const data = new Date(iso);
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  return (
    `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}` +
    `T${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`
  );
}

/** O caminho de volta: o que o `<input>` devolve vira ISO com fuso local. */
export function deCampoLocal(valor: string): string | null {
  if (valor.trim() === '') return null;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data.toISOString();
}
